<template>
  <div class="w-full bg-black/50 justify-center items-center flex">
    <UBreadcrumb :items="items" class="py-2">
      <template #separator>
        <span class="mx-2 text-muted">/</span>
      </template>
    </UBreadcrumb>
  </div>
  <div class="w-full min-h-screen" ref="pageContainerRef">
    <div
      v-html="modelPageData?.content"
      class="content-wrapper"
      ref="contentWrapperRef"
    ></div>
  </div>
</template>

<script setup>
import { useRoute } from "vue-router";
import { watch, onMounted, nextTick, ref, computed, inject } from "vue";
import { enabledModels, normalizeCatalogUrl } from "@/utils/aiCatalogData";

definePageMeta({ layout: "default-new" });

const route = useRoute();
const r2CachedFetch = useR2CachedFetch();

const slugParams = route.params.slug;
const targetPath = Array.isArray(slugParams) ? "/" + slugParams.join("/") : ""; // 防止首页或空 slug 的情况

const cleanPath = targetPath.replace(/\/$/, "");
const apiUrl = `/api/r2/ai/pages/models${cleanPath}.json`;

// 滚动到顶部的函数
const scrollToTop = () => {
  if (typeof window === "undefined") return;
  window.scrollTo({ top: 0, behavior: "smooth" });
};

// 执行滚动的辅助函数
const performScroll = () => {
  // 等待数据加载和 DOM 更新
  nextTick(() => {
    // 再等待 v-html 内容渲染完成
    setTimeout(() => {
      scrollToTop();
    }, 150);
  });
};

// 监听路由参数变化，当 slug 变化时滚动到顶部
watch(
  () => route.params.slug,
  (newSlug, oldSlug) => {
    // 只有当 slug 真正变化时才滚动
    if (JSON.stringify(newSlug) !== JSON.stringify(oldSlug)) {
      performScroll();
    }
  }
);

// 页面首次加载时也滚动到顶部
onMounted(() => {
  performScroll();
  groupData.value;
});

// 2. 数据获取
// key 直接使用 targetPath 字符串，确保唯一性且不使用函数
const { data: modelPageData } = await useAsyncData(
  `modelPageData-${targetPath}`,
  async () => {
    // 如果 targetPath 为空（例如直接访问 /ai），返回 null
    if (!targetPath) {
      return null;
    }

    try {
      let response = await r2CachedFetch(apiUrl);

      // 如果 response 为空，返回 null
      if (!response) {
        return null;
      }

      return response;
    } catch (error) {
      // 捕获 fetch 错误（如 404），返回 null
      console.error("Fetch error:", error);
      return null;
    }
  }
);

// 检查数据是否为空，如果为空则跳转到 404
if (!modelPageData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page Not Found",
    fatal: true,
  });
}

// 监听数据变化，当内容更新时滚动到顶部（用于路由切换时数据重新加载）
watch(
  () => modelPageData.value?.content,
  (newContent, oldContent) => {
    // 只有当内容真正变化时才滚动（避免初始加载时重复滚动）
    if (newContent && newContent !== oldContent) {
      performScroll();
    }
  }
);

// 设置seo
const url = `https://sharkfoto.com/ai/models${cleanPath}/`;
const cdnUrl = useRuntimeConfig().public.cdnUrl;
useSeoMeta({
  title: modelPageData.value?.title,
  description: modelPageData.value?.description,
  ogTitle: modelPageData.value?.title,
  ogDescription: modelPageData.value?.description,
  ogSiteName: "SharkFoto",
  ogType: "website",
  ogLocale: "en_US",
  ogImage: `${cdnUrl}/sharkfoto_og.png`,
  ogUrl: url,
});

const meta = [
  {
    hid: "twitter:card",
    name: "twitter:card",
    content: "summary_large_image",
  },
  {
    hid: "twitter:url",
    name: "twitter:url",
    content: url,
  },
  {
    hid: "twitter:title",
    name: "twitter:title",
    content: modelPageData.value?.title,
  },
  {
    hid: "twitter:image",
    name: "twitter:image",
    content: `${cdnUrl}/sharkfoto_og.png`,
  },
  {
    hid: "twitter:site",
    name: "twitter:site",
    content: "@Shark_Foto",
  },
  {
    hid: "twitter:description",
    name: "twitter:description",
    content: modelPageData.value?.description,
  },
];
useHead({
  link: [
    {
      rel: "canonical",
      href: url,
    },
  ],
  meta: meta,
});

const modelList = inject("modelList", ref([]));
const groupData = computed(() => {
  const currentPath = route.path
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .pop();
  return enabledModels(modelList.value).find((item) => {
    return (
      item?.modelKey === currentPath ||
      normalizeCatalogUrl(item?.url).split("/").pop() === currentPath
    );
  });
});
const items = [
  {
    label: "AI Models",
    to: "/ai/models/",
  },
  {
    label: groupData.value?.groups?.[0],
    to: "/ai/models/",
  },
  {
    label: modelPageData.value?.modelName || groupData.value?.modelName || modelPageData.value?.title?.split(" - ")?.[0]
  },
];
</script>
